import React, { useState } from 'react';
import Question from './Question';
import { submitSurveyResponse } from '../server';
import FinishedSurvey from './FinishedSurvey';
import Loader from './Loader';

function Survey({ surveyData, surveyCode, userName, schoolName, setIsCompleted }) {
	// Ensure surveyData is an array
	const [survey, setSurvey] = useState(Array.isArray(surveyData) ? surveyData : []);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [error, setError] = useState(null);
	const [fullName, setFullName] = useState(userName || '');
	const [email, setEmail] = useState('');
	const [emailError, setEmailError] = useState('');

	const setValueForAnswer = (questionIndex, answer) => {
		// Create a deep copy of the survey array to avoid state mutation issues
		const updatedSurvey = [...survey];

		const questionType = updatedSurvey[questionIndex].type;
		if (questionType === 'singleOptions') {
			updatedSurvey[questionIndex].selectedValue = [answer];
		} else if (questionType === 'multipleOptions') {
			if (!updatedSurvey[questionIndex].selectedValue) {
				updatedSurvey[questionIndex].selectedValue = [];
			}

			const selectedValues = [...(updatedSurvey[questionIndex].selectedValue || [])];
			const index = selectedValues.indexOf(answer);

			if (index === -1) {
				selectedValues.push(answer);
			} else {
				selectedValues.splice(index, 1);
			}

			updatedSurvey[questionIndex].selectedValue = selectedValues;
		}

		setSurvey(updatedSurvey);
	};

	const setAdditionalField = (questionIndex, value) => {
		const updatedSurvey = [...survey];
		updatedSurvey[questionIndex].additionalField = value;
		setSurvey(updatedSurvey);
	};

	const validateEmail = (email) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	const handleEmailChange = (e) => {
		const newEmail = e.target.value;
		setEmail(newEmail);

		if (newEmail && !validateEmail(newEmail)) {
			setEmailError('Please enter a valid email address');
		} else {
			setEmailError('');
		}
	};

	// Check if all required questions have been answered
	const isFormValid = () => {
		if (!Array.isArray(survey)) return false;
		if (!fullName.trim()) return false;
		if (!email.trim() || !validateEmail(email)) return false;

		for (let i = 0; i < survey.length; i++) {
			const question = survey[i];

			// Skip validation for non-required questions
			if (question.required === false) continue;

			// Check if the question has any selected value
			if (!question.selectedValue || question.selectedValue.length === 0) {
				return false;
			}

			// Special validation for questions with "Other" option
			if (question.leaveComment && question.selectedValue.includes('OTHER_CUSTOM_VALUE')) {
				// If "Other" is selected, ensure the additionalField has content (not empty or just spaces)
				if (!question.additionalField || !question.additionalField.trim()) {
					return false;
				}
			}
		}
		return true;
	};

	const prepareDataForSubmission = () => {
		// Create a deep copy for submission to avoid modifying state
		const submissionData = survey.map((question) => {
			const questionCopy = { ...question };

			// Replace the "OTHER_CUSTOM_VALUE" placeholder with actual "Other" text
			if (question.leaveComment && question.selectedValue && question.selectedValue.includes('OTHER_CUSTOM_VALUE')) {
				// Remove the placeholder and add the actual "Other" text
				const otherIndex = questionCopy.selectedValue.indexOf('OTHER_CUSTOM_VALUE');
				if (otherIndex !== -1) {
					questionCopy.selectedValue[otherIndex] = 'Other: ' + question.additionalField.trim();
				}
			}

			return questionCopy;
		});

		return submissionData;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!isFormValid()) {
			setError('Please answer all required questions, provide your full name, and enter a valid email address.');
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			// Process the data for submission
			const processedData = prepareDataForSubmission();

			// Include the user name and school name in the submission
			const submissionData = {
				surveyResponses: processedData,
				userName: fullName,
				userEmail: email,
				schoolName: schoolName,
			};

			await submitSurveyResponse(surveyCode, submissionData);
			setIsSubmitted(true);
			setIsCompleted(true);
		} catch (err) {
			setError('Failed to submit survey. Please try again.');
			console.error('Error submitting survey:', err);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Add a check here to handle non-array survey data
	if (!Array.isArray(survey)) {
		return (
			<div>
				<p className='text-red-500 text-center'>Error: Survey data is not in the expected format.</p>
				<p className='text-center'>Please contact support for assistance.</p>
			</div>
		);
	}

	return (
		<div>
			<form onSubmit={handleSubmit}>
				{survey.map((question, index) => (
					<Question key={index} order={index + 1} title={question.title} questions={question.options} setSelectedValue={(value) => setValueForAnswer(index, value)} selectedValue={question.selectedValue || []} additionalFieldAllowed={question.leaveComment} additionalFieldValue={question.additionalField || ''} setAdditionalField={(value) => setAdditionalField(index, value)} />
				))}

				<div className='mt-8 mb-8'>
					<h2 className='text-2xl font-bold mb-4'>Before submitting your survey, please provide the following details:</h2>

					<div className='mb-4 flex flex-row items-center'>
						<label htmlFor='fullName' className='font-medium min-w-[150px]'>
							Your full name <span className='text-red-500'>*</span>
						</label>
						<input type='text' id='fullName' placeholder='Enter details here...' className='border-b flex-1 px-3 py-2' value={fullName} onChange={(e) => setFullName(e.target.value)} required />
					</div>

					<div className='mb-4'>
						<div className='flex flex-row items-center'>
							<label htmlFor='email' className='font-medium min-w-[150px]'>
								Your email address <span className='text-red-500'>*</span>
							</label>
							<input type='email' id='email' placeholder='Enter details here...' className={`border-b flex-1 px-3 py-2 ${emailError ? 'border-red-500' : ''}`} value={email} onChange={handleEmailChange} required />
						</div>
						{emailError && <p className='text-red-500 text-sm mt-1 ml-[150px]'>{emailError}</p>}
					</div>
				</div>

				{error && <div style={{ color: 'red', marginBottom: '20px', textAlign: 'center' }}>{error}</div>}

				<div style={{ textAlign: 'center', marginTop: '30px' }}>
					<button type='submit' className={`confirm-button ${isSubmitting || !isFormValid() ? 'disabled-button' : ''}`} disabled={isSubmitting || !isFormValid()}>
						{isSubmitting ? <Loader size={20} /> : 'Submit Survey'}
					</button>
				</div>
			</form>
		</div>
	);
}

export default Survey;
